import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { supabase } from '../../supabaseClient'
import {
  J, ReflectionBlock, ViewToggle, AiEngagement, FeedbackRounds, handlePrint, fmtDate, fmtDateTime,
  StepReflection, JournalNote, UsageRow, FeedbackRow,
} from '../../components/journal/paper'
import StepReflectionCard from '../../components/journal/StepReflectionCard'

// Step 3 research journal — curated Journal view (background research narrative +
// judge-readiness) and a Documentation (audit) view (judge-question detail,
// mentor review record incl. per-section comments, AI engagement + ISEF
// disclosure). Built on the shared journal paper kit.

interface PaperRef { url?: string; title?: string; abstract?: string }
interface JudgeQuestion { question: string; coverage?: string; where?: string; improve?: string }
interface JudgeReadiness {
  covered_count?: number; total?: number
  band?: 'winning_ready' | 'almost' | 'some_gaps' | 'thin'
  summary?: string; questions?: JudgeQuestion[]
}
interface Payload {
  notebooklm_url?: string; keywords?: string[]
  review_paper?: PaperRef; model_paper?: PaperRef
  significance?: string; key_concepts?: string; principles?: string
  what_research_shows?: string; the_gap?: string; conclusion?: string
  refined_question?: string; purpose?: string
  judge_readiness?: JudgeReadiness
}
interface ReviewEntry { action: string; note: string | null; acted_at: string }

const BAND_LABEL: Record<string, string> = {
  winning_ready: 'Ready for judges',
  almost:        'Almost there',
  some_gaps:     'Some gaps to close',
  thin:          'Needs more depth',
}
const SECTION_LABEL: Record<string, string> = {
  introduction: 'Introduction', background: 'Background', literature: 'Literature', conclusion: 'Conclusion',
}
const FIELD_LABELS: Record<string, string> = {
  keywords: 'Search keywords', review_paper: 'Review / meta-analysis paper', model_paper: 'Model experiment paper',
  introduction: 'Introduction', background: 'Background', literature: 'Literature', conclusion: 'Conclusion',
  refined_question: 'Refined question', feasibility: 'Feasibility', sources: 'Sources', synthesis: 'Synthesis',
}
const AI_LABELS: Record<string, string> = {
  'step3-evaluate-field':   'Section feedback',
  'step3-judge-readiness':  'Judge-readiness check',
  'step3-recommend-paper':  'Source paper recommendations',
  'step3-generate-paper':   'Paper draft assembly',
  'step3-mentor-ai-review': 'Mentor AI review',
  'step3-validity-check':   'Validity check',
}

export default function JournalTab({ projectId, studentId, isEng }: {
  projectId: string; studentId: string; isEng: boolean
}) {
  const [payload,    setPayload]    = useState<Payload | null>(null)
  const [status,     setStatus]     = useState<string>('draft')
  const [mentorNote, setMentorNote] = useState<string | null>(null)
  const [sectionComments, setSectionComments] = useState<Record<string, string>>({})
  const [notes,      setNotes]      = useState<JournalNote[]>([])
  const [reflection, setReflection] = useState<StepReflection | null>(null)
  const [reviewLog,  setReviewLog]  = useState<ReviewEntry[]>([])
  const [usage,      setUsage]      = useState<UsageRow[]>([])
  const [feedback,   setFeedback]   = useState<FeedbackRow[]>([])
  const [updatedAt,  setUpdatedAt]  = useState<string | null>(null)
  const [loading,    setLoading]    = useState(true)
  const [view,       setView]       = useState<'journal' | 'documentation'>('journal')

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [{ data: br }, { data: noteRows }, { data: refl }, { data: log }, { data: usageRows }, { data: fbRows }] = await Promise.all([
        supabase.from('background_research').select('payload, status, mentor_note, mentor_section_comments, updated_at').eq('project_id', projectId).maybeSingle(),
        supabase.from('journal_note').select('note_id, step_number, source_label, content, saved_at')
          .eq('project_id', projectId).eq('step_number', 3).order('saved_at'),
        supabase.from('step_reflection').select('*').eq('project_id', projectId).eq('step_number', 3).maybeSingle(),
        supabase.from('mentor_review_log').select('action, note, acted_at').eq('project_id', projectId).in('action', ['comment', 'approve']).order('acted_at'),
        supabase.from('ai_usage_log').select('function_name, model, input_tokens, output_tokens').eq('project_id', projectId).like('function_name', 'step3%'),
        supabase.from('ai_feedback_log').select('field, evaluation, created_at').eq('project_id', projectId).eq('step_number', 3).order('created_at'),
      ])
      if (cancelled) return
      setPayload(((br as any)?.payload ?? {}) as Payload)
      setStatus(((br as any)?.status ?? 'draft'))
      setMentorNote(((br as any)?.mentor_note ?? null))
      setSectionComments(((br as any)?.mentor_section_comments ?? {}) as Record<string, string>)
      setUpdatedAt((br as any)?.updated_at ?? null)
      setNotes((noteRows as JournalNote[] | null) ?? [])
      setReflection((refl as StepReflection | null) ?? null)
      setReviewLog((log as ReviewEntry[] | null) ?? [])
      setUsage((usageRows as UsageRow[] | null) ?? [])
      setFeedback((fbRows as FeedbackRow[] | null) ?? [])
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [projectId, studentId])

  async function reloadReflection() {
    const { data: refl } = await supabase.from('step_reflection').select('*')
      .eq('project_id', projectId).eq('step_number', 3).maybeSingle()
    setReflection((refl as StepReflection | null) ?? null)
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>

  const p        = payload ?? {}
  const qLabel   = isEng ? 'Refined problem statement' : 'Refined research question'
  const dateStr  = updatedAt ? fmtDate(updatedAt) : fmtDate(new Date().toISOString())
  const complete = status === 'approved_draft' || status === 'completed'
  const isDoc    = view === 'documentation'
  const jr       = p.judge_readiness
  const jrQuestions = (jr?.questions ?? []).filter(q => q?.question)
  const commentEntries = Object.entries(sectionComments).filter(([, v]) => (v ?? '').trim())

  const sections: Array<{ h: string; body?: string }> = [
    { h: 'Why this research matters', body: p.significance },
    { h: 'Background & key concepts', body: p.key_concepts },
    { h: isEng ? 'Relevant principles' : 'Scientific principles', body: p.principles },
    { h: 'What previous work shows', body: p.what_research_shows },
    { h: 'The gap & where my work fits', body: p.the_gap },
    { h: 'Conclusion', body: p.conclusion },
  ]
  const papers = [
    { tag: 'Review / meta-analysis', paper: p.review_paper },
    { tag: 'Model experimental', paper: p.model_paper },
  ].filter(x => (x.paper?.title || x.paper?.url || '').trim())
  const keywords = (p.keywords ?? []).filter(Boolean)
  const anyContent = !!(p.refined_question || sections.some(s => s.body?.trim()))

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {isDoc ? 'Research Documentation' : 'Your Research Journal'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {isDoc
              ? 'Complete system record for this step — judge-question coverage, mentor review, AI engagement, and ISEF Rule 4.2 disclosure.'
              : "A curated record of your background research — what your reading found, your key sources, and how ready your work is for judges. Print it for your notebook, or copy what's meaningful."}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ViewToggle value={view} onChange={setView} />
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => handlePrint('journal-step3-print')}>Print</Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <StepReflectionCard
          projectId={projectId}
          stepNumber={3}
          stepLabel="Step 3: Background Research"
          onSavedChange={() => { reloadReflection() }}
        />
      </Box>

      <J.Page id="journal-step3-print">
        <J.Header
          title={isDoc ? 'STEP 3 DOCUMENTATION' : 'SCIENCE RESEARCH JOURNAL'}
          subtitle={isDoc ? 'Complete record — Background Research' : 'Step 3 — Background Research'}
          date={dateStr}
        />

        <J.Section>{qLabel.toUpperCase()}</J.Section>
        <J.Body sx={{ fontWeight: 'bold', fontSize: '12pt' }}>{p.refined_question || '—'}</J.Body>
        {p.purpose?.trim() && <J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.purpose}</J.Body>}

        <J.Section>WHAT MY BACKGROUND RESEARCH FOUND</J.Section>
        <J.Body>Through my background reading, I built the following understanding of my topic:</J.Body>
        {sections.map(s => s.body?.trim() && (
          <Box key={s.h} className="journal-no-break" sx={{ mb: 1.25 }}>
            <J.Label>{s.h}</J.Label>
            <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{s.body}</J.Body>
          </Box>
        ))}

        {papers.length > 0 && (
          <>
            <J.Section>KEY SOURCES</J.Section>
            {papers.map((x, i) => (
              <J.Body key={i} sx={{ mb: 0.5 }}>
                <strong>{x.tag}:</strong> {x.paper?.title || x.paper?.url}
                {x.paper?.title && x.paper?.url ? ` (${x.paper.url})` : ''}
              </J.Body>
            ))}
            {p.notebooklm_url && <J.Body sx={{ fontSize: '10pt', color: '#444' }}>All sources: {p.notebooklm_url}</J.Body>}
            {keywords.length > 0 && (<><J.Label>Keywords</J.Label><J.Body>{keywords.join(' · ')}</J.Body></>)}
          </>
        )}

        {jr && (jr.summary || jr.covered_count != null) && (
          <>
            <J.Section>HOW READY MY RESEARCH IS FOR JUDGES</J.Section>
            {jr.band && (
              <J.Body sx={{ fontWeight: 'bold' }}>
                {BAND_LABEL[jr.band] ?? jr.band}
                {jr.covered_count != null && jr.total != null && ` — ${jr.covered_count} of ${jr.total} likely judge questions well-covered`}
              </J.Body>
            )}
            {jr.summary && <J.Body sx={{ whiteSpace: 'pre-wrap' }}>{jr.summary}</J.Body>}

            {isDoc && jrQuestions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <J.Label>Question-by-question coverage</J.Label>
                {jrQuestions.map((q, i) => (
                  <Box key={i} className="journal-no-break" sx={{ mb: 1 }}>
                    <J.Body sx={{ fontWeight: 'bold', fontSize: '10.5pt', mb: 0 }}>
                      {i + 1}. {q.question}{q.coverage ? ` — ${q.coverage}` : ''}
                    </J.Body>
                    {q.where && <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 0 }}>Where addressed: {q.where}</J.Body>}
                    {q.improve && <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 0 }}>To strengthen: {q.improve}</J.Body>}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        <ReflectionBlock refl={reflection} notes={notes} />

        {isDoc && (mentorNote || reviewLog.length > 0 || commentEntries.length > 0) && (
          <>
            <J.Section>MENTOR REVIEW RECORD</J.Section>
            {reviewLog.map((r, i) => (
              <Box key={i} className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>{r.action === 'approve' ? 'Approved' : 'Sent back with comments'} · {fmtDateTime(r.acted_at)}</J.Label>
                {r.note && <J.Quote>{r.note}</J.Quote>}
              </Box>
            ))}
            {mentorNote && reviewLog.length === 0 && <J.Quote>{mentorNote}</J.Quote>}
            {commentEntries.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <J.Label>Per-section mentor comments</J.Label>
                {commentEntries.map(([k, v]) => (
                  <Box key={k} className="journal-no-break" sx={{ mb: 0.75 }}>
                    <J.Body sx={{ fontWeight: 'bold', fontSize: '10pt', mb: 0 }}>{SECTION_LABEL[k] ?? k}</J.Body>
                    <J.Body sx={{ fontSize: '10pt', color: '#333', whiteSpace: 'pre-wrap', mb: 0 }}>{v}</J.Body>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {isDoc && <FeedbackRounds rows={feedback} fieldLabels={FIELD_LABELS} />}

        {isDoc && <AiEngagement usage={usage} labels={AI_LABELS} date={dateStr} />}

        {complete && <J.Signatures />}

        {!complete && anyContent && (
          <J.Body sx={{ mt: 2, fontSize: '10pt', color: '#555', fontStyle: 'italic' }}>
            This entry updates as you refine your background research. The final version — with signatures — is ready
            once your mentor approves this step.
          </J.Body>
        )}
      </J.Page>

      {!anyContent && (
        <Alert severity="info" sx={{ mt: 2 }}>Your Step 3 journal fills in as you complete your background research.</Alert>
      )}
    </Box>
  )
}
